import { useMemo } from "react";
import { AppEventPublisher } from "src/AppEventPublisher";
import { useEntityImageGeneration, type EntityImageGenerationAdapter, type UseEntityImageGenerationResult } from "../../../EntityImage/lib/useEntityImageGeneration";
import { NO_STYLE_PRESET_ID, generateCharacterPortrait, listCharacterImageStylePresets, saveGeneratedCharacterPortrait, summarizeCharacterVisuals, uploadCharacterPortrait } from "./api";
import type { SummarizeResponse } from "./api";

export type { ItemSaveStatus, TileState, TileStatus } from "../../../EntityImage/lib/types";
export type { MultiSaveResult } from "../../../EntityImage/lib/useEntityImageGeneration";
export type UsePortraitGenerationResult = UseEntityImageGenerationResult;

const COPY = {
  allFailed: "Couldn't generate any portraits. Please try again.",
  allFailedWithReference: "Couldn't generate any portraits using that reference photo. Try a different image, or remove the reference image and generate again.",
  someFailedWithReference: "Some styles couldn't be generated using that reference photo. Retry them, or try a different reference image.",
  unsavedTiles: "One or more portraits generated but weren't saved — retry them before generating again or closing, or they'll be lost.",
};

function createCharacterPortraitAdapter(
  characterId: string,
  { physicalDescription, pronouns }: { physicalDescription?: string | null; pronouns?: string | null },
): EntityImageGenerationAdapter<SummarizeResponse, undefined> {
  return {
    listStylePresets: () => listCharacterImageStylePresets(characterId),
    summarize: ({ description, referenceImage }) => {
      // Traits are blanked (explicit "", not omitted) when a reference image is attached, so the
      // photo — not competing stored trait text — drives appearance. The server only falls back
      // to stored traits on a missing key, not an empty string.
      const traits = referenceImage ? { physicalDescription: "", pronouns: "" } : { physicalDescription, pronouns };
      return summarizeCharacterVisuals(characterId, description, traits, !!referenceImage);
    },
    generate: (summary, request) =>
      generateCharacterPortrait(characterId, {
        subject: summary.subject,
        mood: summary.mood,
        background: summary.background,
        stylePresetId: request.stylePresetId,
        stylePresetText: request.stylePresetText,
        referenceImage: request.referenceImage,
        referenceImageGuidance: request.referenceImageGuidance,
        userDescription: request.userDescription,
        batchId: request.batchId,
      }),
    saveGenerated: (payload) => saveGeneratedCharacterPortrait(characterId, payload),
    upload: (payload) => uploadCharacterPortrait(characterId, payload),
    analytics: {
      generateClicked: (props) => void AppEventPublisher.characterImage.generateClicked({ characterId, ...props, hasPhysicalDescription: !!physicalDescription }),
      tileResult: (props) => void AppEventPublisher.characterImage.tileResult({ characterId, ...props }),
      retryTileClicked: (props) => void AppEventPublisher.characterImage.retryTileClicked({ characterId, ...props }),
      saveBatchCompleted: (props) => void AppEventPublisher.characterImage.saveBatchCompleted({ characterId, ...props }),
    },
    sentryFeaturePrefix: "character_portrait",
    // Zero selected styles is valid — it produces one tile using NO_STYLE_PRESET_ID instead of
    // blocking Generate (see buildDefaultCharacterImagePrompt server-side).
    freeformStyle: { id: NO_STYLE_PRESET_ID, label: "Portrait" },
    defaultStyleIds: ["painterly-illustration"],
    maxSelectedStyles: 4,
    copy: COPY,
  };
}

export function usePortraitGeneration(
  characterId: string,
  {
    onSaved,
    onGenerated,
    onCoverSet,
    physicalDescription,
    pronouns,
  }: {
    onSaved?: (result: { imageId: string; imageUrl: string } | null, meta: { hadFailures: boolean }) => void;
    /** Fired when a generate batch (or a single Retry) saved at least one tile. */
    onGenerated?: () => void;
    onCoverSet?: (cover: { imageId: string; imageUrl: string }) => void;
    physicalDescription?: string | null;
    pronouns?: string | null;
  } = {},
): UsePortraitGenerationResult {
  const adapter = useMemo(() => createCharacterPortraitAdapter(characterId, { physicalDescription, pronouns }), [characterId, physicalDescription, pronouns]);
  return useEntityImageGeneration(adapter, { onSaved, onGenerated, onCoverSet });
}
